import { useState, useEffect } from 'react';
import { fetchApi } from '../lib/api';

export default function EventLandingPage({ role, hasSubscription, onNavigate }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchApi('/api/events/all-published')
      .then(res => setEvents(res.events || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center py-20"><p className="text-sm text-gray-400">Loading events...</p></div>;
  }

  if (events.length === 0) {
    return (
      <div className="glass p-8 max-w-lg mx-auto mt-8 text-center">
        <div className="text-4xl mb-4">🎪</div>
        <h2 className="font-heading text-2xl text-vault-navy mb-3">No upcoming events</h2>
        <p className="text-sm text-gray-400 leading-relaxed">
          Check back soon — events are announced regularly.
        </p>
      </div>
    );
  }

  const formatDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';

  const handleEventClick = (evt) => {
    if (!evt.slug) return;

    if (evt.has_access) {
      // User has paid — go to their in-app event dashboard
      if (role === 'inventor') {
        onNavigate('events');
      } else if (role === 'company') {
        onNavigate('events');
      }
      return;
    }

    // Not paid — open the public event page for their role
    if (role === 'company') {
      window.open(`/event/${evt.slug}/companies`, '_blank');
    } else {
      window.open(`/event/${evt.slug}/inventors`, '_blank');
    }
  };

  const now = new Date();

  return (
    <div>
      <h1 className="font-heading text-3xl text-vault-navy mb-2">Events</h1>
      <p className="text-sm text-gray-400 mb-8">Upcoming pitch events and opportunities</p>

      <div className="grid gap-5 max-w-3xl">
        {events.map(evt => {
          const startDate = evt.start_date ? new Date(evt.start_date + 'T00:00:00') : null;
          const endDate = evt.end_date ? new Date(evt.end_date + 'T00:00:00') : null;
          const isUpcoming = startDate && startDate > now;
          const isLive = startDate && endDate && now >= startDate && now <= endDate;
          const isPast = endDate && now > endDate;

          return (
            <div
              key={evt.id}
              onClick={() => handleEventClick(evt)}
              className="glass p-0 overflow-hidden cursor-pointer hover:shadow-glass-hover transition-all group"
            >
              {/* Hero image */}
              {evt.hero_image_url && (
                <div className="h-40 bg-gray-100 overflow-hidden">
                  <img src={evt.hero_image_url} alt={evt.event_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
              )}

              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h2 className="font-heading text-xl text-vault-navy group-hover:text-vault-gold transition-colors">{evt.event_name}</h2>
                    <p className="text-sm text-vault-gold font-semibold mt-1">
                      {formatDate(evt.start_date)}
                      {evt.end_date && evt.end_date !== evt.start_date ? ` — ${formatDate(evt.end_date)}` : ''}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 shrink-0 ml-4">
                    {/* Status badge */}
                    {isLive && (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">Live</span>
                    )}
                    {isUpcoming && (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">Upcoming</span>
                    )}
                    {isPast && (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">Completed</span>
                    )}

                    {/* Access badge */}
                    {evt.has_access ? (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">Registered</span>
                    ) : (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 flex items-center gap-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
                        Register
                      </span>
                    )}
                  </div>
                </div>

                {evt.description && (
                  <p className="text-sm text-gray-400 leading-relaxed line-clamp-2">{evt.description}</p>
                )}

                {!evt.has_access && (
                  <p className="text-xs text-gray-400 mt-3">
                    {role === 'company'
                      ? 'Click to register and book your time slot'
                      : 'Click to register and submit your idea'}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
